import React, { useMemo, useState } from "react";
import { AlertCircle, Calendar, ClipboardPaste, Copy, Plus } from "lucide-react";
import { ChapterCard, SectionCard, UnitCard } from "./Cards";
import ChapterFields from "./ChapterFields";
import { ConfirmDelete, inputCls } from "./inputs";
import { chapterIssues, chaptersOfTerm, describeIssues, newChapter, newUnit, parseChapterList } from "./planModel";

export { mkId, PEDAGOGY_OPTIONS, chapterIssues } from "./planModel";

function BulkAdd({ onCancel, onAdd, termNo }) {
  const [text, setText] = useState("");
  const names = parseChapterList(text);
  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Paste chapter list">
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Cancel" onClick={onCancel} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="m-0 font-[poppins] text-xl font-semibold text-gray-900">Add several chapters at once</h3>
        <p className="mb-4 mt-1 font-[poppins] text-sm text-gray-600">
          Paste or type one chapter per line for Term {termNo}. Numbers at the start of a line are ignored.
        </p>
        <textarea
          autoFocus
          rows={8}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"1. Numbers up to 100\n2. Addition and subtraction\n3. Shapes around us"}
          className={inputCls}
        />
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-500">{names.length ? `${names.length} chapter${names.length === 1 ? "" : "s"} found` : "Nothing to add yet"}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} className="rounded-xl border border-gray-300 px-4 py-2 font-[poppins] text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="button" disabled={!names.length} onClick={() => onAdd(names)} className="rounded-xl bg-[#1565c0] px-4 py-2 font-[poppins] text-sm font-semibold text-white hover:bg-[#0f4f99] disabled:opacity-50">
              Add {names.length || ""} chapter{names.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAdd({ onAdd }) {
  const [text, setText] = useState("");
  const submit = () => {
    const v = text.trim();
    if (!v) return;
    onAdd(v);
    setText("");
  };
  return (
    <div className="flex items-center gap-2">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        placeholder="Type a new chapter name and press Enter"
        aria-label="Add a chapter"
        className={`${inputCls} border-dashed`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={!text.trim()}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#1565c0] px-4 py-3 font-[poppins] text-sm font-semibold text-white hover:bg-[#0f4f99] disabled:opacity-40"
      >
        <Plus className="h-4 w-4" /> Add
      </button>
    </div>
  );
}

/**
 * Plan builder as nested, expandable cards (Term → Unit → Chapter → fields),
 * styled like the lesson plan creator. Nothing opens in a drawer or modal.
 * `onPlanChange(draft => …)` mutates a deep clone of the plan.
 */
export default function PlanEditor({ plan, locked, onPlanChange, competencyOptions = [], onCopy }) {
  const firstActive = plan.terms.find((t) => t.units.some((u) => u.chapters.length))?.termNo || 1;
  const [openTerms, setOpenTerms] = useState(() => new Set([firstActive]));
  const [openChapters, setOpenChapters] = useState(() => new Set());
  const [bulkTerm, setBulkTerm] = useState(null);

  const stats = useMemo(() => {
    let total = 0;
    let ready = 0;
    const byTerm = {};
    plan.terms.forEach((t) => {
      const all = chaptersOfTerm(t);
      const r = all.filter((x) => chapterIssues(x.chapter).length === 0).length;
      byTerm[t.termNo] = { total: all.length, ready: r };
      total += all.length;
      ready += r;
    });
    return { total, ready, byTerm };
  }, [plan.terms]);

  const toggle = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const inTerm = (termNo, fn) => onPlanChange((draft) => fn(draft.terms.find((x) => x.termNo === termNo)));

  const addChapters = (termNo, names, unitId) => {
    const created = names.map((n) => newChapter(n));
    inTerm(termNo, (t) => {
      let unit = unitId ? t.units.find((u) => u.id === unitId) : t.units[t.units.length - 1];
      if (!unit) {
        unit = newUnit(t.units.length + 1);
        t.units.push(unit);
      }
      created.forEach((c) => unit.chapters.push(c));
    });
    setOpenTerms((prev) => new Set(prev).add(termNo));
    // Open the chapter only when a single one was added, so typing several in a row stays smooth.
    if (created.length === 1) setOpenChapters((prev) => new Set(prev).add(created[0].id));
  };

  const jumpToNext = () => {
    for (const t of plan.terms) {
      const hit = chaptersOfTerm(t).find((x) => chapterIssues(x.chapter).length);
      if (hit) {
        setOpenTerms((prev) => new Set(prev).add(t.termNo));
        setOpenChapters((prev) => new Set(prev).add(hit.chapter.id));
        setTimeout(() => document.getElementById(`ch-${hit.chapter.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        return;
      }
    }
  };

  const needsDetails = stats.total - stats.ready;

  return (
    <div className="font-[poppins]">
      {needsDetails > 0 && !locked && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3.5">
          <span className="flex items-center gap-2 text-sm font-medium text-amber-900">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
            {needsDetails} chapter{needsDetails === 1 ? " needs" : "s need"} more details before you can submit.
          </span>
          <button type="button" onClick={jumpToNext} className="text-sm font-semibold text-[#1565c0] hover:underline">
            Show me the next one →
          </button>
        </div>
      )}

      {plan.terms.map((term) => {
        const s = stats.byTerm[term.termNo];
        const empty = term.units.every((u) => u.chapters.length === 0);
        return (
          <SectionCard
            key={term.termNo}
            icon={Calendar}
            title={`Term ${term.termNo}`}
            subtitle={s.total ? `${s.total} chapter${s.total === 1 ? "" : "s"} · ${s.ready} ready` : "No chapters yet"}
            badge={
              s.total > 0 ? (
                <span className={`mr-2 hidden rounded-full px-3 py-1 text-xs font-semibold sm:inline ${s.ready === s.total ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {s.ready === s.total ? "Complete" : `${s.total - s.ready} to finish`}
                </span>
              ) : null
            }
            open={openTerms.has(term.termNo)}
            onToggle={() => toggle(setOpenTerms, term.termNo)}
          >
            {empty && !locked && (
              <div className="mb-6 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center">
                <p className="m-0 text-lg font-semibold text-gray-800">No chapters in Term {term.termNo} yet</p>
                <p className="mx-auto mb-5 mt-1 max-w-md text-sm text-gray-500">Pick whichever is quickest for you.</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {onCopy && (
                    <button type="button" onClick={onCopy} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                      <Copy className="h-4 w-4" /> Copy from another class
                    </button>
                  )}
                  <button type="button" onClick={() => setBulkTerm(term.termNo)} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                    <ClipboardPaste className="h-4 w-4" /> Paste a list of chapters
                  </button>
                </div>
                <p className="mb-0 mt-5 text-xs text-gray-500">…or type a chapter name below and press Enter.</p>
              </div>
            )}
            {empty && locked && <p className="m-0 text-sm text-gray-500">Nothing planned for Term {term.termNo}.</p>}

            {term.units.map((unit) => (
              <UnitCard
                key={unit.id}
                header={
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-lg bg-[#1565c0] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">{unit.unitNo || "Unit"}</span>
                    <input
                      value={unit.unitName}
                      disabled={locked}
                      onChange={(e) => inTerm(term.termNo, (t) => { t.units.find((u) => u.id === unit.id).unitName = e.target.value; })}
                      placeholder="Unit name (optional)"
                      aria-label="Unit name"
                      className={`${inputCls} min-w-[12rem] flex-1 !bg-white`}
                    />
                    <span className="text-sm text-gray-500">{unit.chapters.length} chapter{unit.chapters.length === 1 ? "" : "s"}</span>
                    <ConfirmDelete label="Remove unit" disabled={locked} onConfirm={() => inTerm(term.termNo, (t) => { t.units = t.units.filter((u) => u.id !== unit.id); })} />
                  </div>
                }
              >
                {unit.chapters.map((ch) => {
                  const issues = chapterIssues(ch);
                  const ready = issues.length === 0;
                  return (
                    <ChapterCard
                      key={ch.id}
                      id={`ch-${ch.id}`}
                      ready={ready}
                      open={openChapters.has(ch.id)}
                      onToggle={() => toggle(setOpenChapters, ch.id)}
                      title={`${ch.chapterNo ? `${ch.chapterNo}. ` : ""}${ch.chapterName || "Untitled chapter"}`}
                      meta={
                        ready
                          ? `${ch.learningObjectives.filter(Boolean).length} learning goals · ${ch.competencies.length + ch.customCompetencies.length} competencies`
                          : `Still needed: ${describeIssues(issues)}`
                      }
                      actions={
                        <ConfirmDelete
                          label="Delete chapter"
                          disabled={locked}
                          onConfirm={() => inTerm(term.termNo, (t) => { const u = t.units.find((x) => x.id === unit.id); u.chapters = u.chapters.filter((c) => c.id !== ch.id); })}
                        />
                      }
                    >
                      <ChapterFields
                        chapter={ch}
                        locked={locked}
                        competencyOptions={competencyOptions}
                        onChange={(fn) =>
                          inTerm(term.termNo, (t) => {
                            const c = t.units.find((x) => x.id === unit.id)?.chapters.find((x) => x.id === ch.id);
                            if (c) fn(c);
                          })
                        }
                      />
                    </ChapterCard>
                  );
                })}
                {!locked && <QuickAdd onAdd={(name) => addChapters(term.termNo, [name], unit.id)} />}
              </UnitCard>
            ))}

            {term.units.length === 0 && !locked && <QuickAdd onAdd={(name) => addChapters(term.termNo, [name])} />}

            {!locked && !empty && (
              <div className="mt-2 flex flex-wrap items-center gap-5 text-sm">
                <button type="button" onClick={() => setBulkTerm(term.termNo)} className="inline-flex items-center gap-1.5 font-semibold text-[#1565c0] hover:underline">
                  <ClipboardPaste className="h-4 w-4" /> Paste a list of chapters
                </button>
                <button type="button" onClick={() => inTerm(term.termNo, (t) => t.units.push(newUnit(t.units.length + 1)))} className="inline-flex items-center gap-1.5 font-semibold text-gray-600 hover:underline">
                  <Plus className="h-4 w-4" /> Add another unit
                </button>
              </div>
            )}
          </SectionCard>
        );
      })}

      {bulkTerm && (
        <BulkAdd termNo={bulkTerm} onCancel={() => setBulkTerm(null)} onAdd={(names) => { addChapters(bulkTerm, names); setBulkTerm(null); }} />
      )}
    </div>
  );
}
